import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ChatProgressStatus = 'progress' | 'done';

export interface ChatProgressEntry {
  status: ChatProgressStatus;
  query?: string; // The user's query text for in-progress chats
  threadId?: string; // The real thread_id once known during streaming
  statusMessages?: string[]; // SSE status messages collected during streaming
}

interface ChatProgressState {
  statuses: Record<string, ChatProgressStatus>;
  entries: Record<string, ChatProgressEntry>; // New: stores status + query text
}

const STORAGE_KEY = 'chat_progress_statuses';
const ENTRIES_STORAGE_KEY = 'chat_progress_entries';

function loadFromStorage(): Record<string, ChatProgressStatus> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function loadEntriesFromStorage(): Record<string, ChatProgressEntry> {
  try {
    const raw = localStorage.getItem(ENTRIES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveToStorage(statuses: Record<string, ChatProgressStatus>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(statuses));
  } catch { /* silently ignore quota errors */ }
}

function saveEntriesToStorage(entries: Record<string, ChatProgressEntry>) {
  try {
    localStorage.setItem(ENTRIES_STORAGE_KEY, JSON.stringify(entries));
  } catch { /* silently ignore quota errors */ }
}

const initialState: ChatProgressState = {
  statuses: loadFromStorage(),
  entries: loadEntriesFromStorage(),
};

const chatProgressSlice = createSlice({
  name: 'chatProgress',
  initialState,
  reducers: {
    setChatProgress(state, action: PayloadAction<{ chatId: string; status: ChatProgressStatus; query?: string; threadId?: string; statusMessages?: string[] }>) {
      state.statuses[action.payload.chatId] = action.payload.status;
      state.entries[action.payload.chatId] = {
        status: action.payload.status,
        query: action.payload.query || state.entries[action.payload.chatId]?.query,
        threadId: action.payload.threadId || state.entries[action.payload.chatId]?.threadId,
        statusMessages: action.payload.statusMessages || state.entries[action.payload.chatId]?.statusMessages,
      };
      saveToStorage(state.statuses);
      saveEntriesToStorage(state.entries);
    },
    clearChatProgress(state, action: PayloadAction<string>) {
      delete state.statuses[action.payload];
      delete state.entries[action.payload];
      saveToStorage(state.statuses);
      saveEntriesToStorage(state.entries);
    },
    clearAllChatProgress(state) {
      state.statuses = {};
      state.entries = {};
      saveToStorage(state.statuses);
      saveEntriesToStorage(state.entries);
    },
  },
});

export const { setChatProgress, clearChatProgress, clearAllChatProgress } = chatProgressSlice.actions;

export default chatProgressSlice.reducer;
